import {Suspense} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {useNavigate} from 'react-router';
import {PermissionTypeKr} from '@di-shared/api/di/type';
import {ERROR} from '@di-shared/const';
import {ExpectedError} from '@di-shared/features/common/CustomError';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import useTripDetailKr from '@di-shared/features/history/date/useTripDetail.kr';
import {DrivingInfoTripContentProps} from '@di-shared/features/history/driving-info/DrivingInfoTripContent/index';
import useAvailableComponentKr from '@di-shared/features/home/kr/useAvailableComponent.kr';
import {ROUTE_PATHS} from '@di-shared/routes';
import {formatNumberWithCommas} from '@di-shared/utils';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import useDashedDatetimeFormatter from '@di-shared/utils/useDashedDatetimeFormatter';
import useUnitConverter from '@di-shared/utils/useUnitConverter';
import {isSameDate, parseDateWithFormat} from '@shared/dateUtils';

export default function ({vin, selectedDate}: DrivingInfoTripContentProps) {
  return (
    <ErrorBoundary fallback={<FallbackError selectedDate={selectedDate} />}>
      <Suspense fallback={<FallbackLoading />}>
        <Inner
          vin={vin}
          selectedDate={selectedDate}
        />
      </Suspense>
    </ErrorBoundary>
  );
}

function Inner({vin, selectedDate}: DrivingInfoTripContentProps) {
  const {isEmptyTrip} = useTripDetailKr({vin, selectedDate});

  // No data 화면
  if (isEmptyTrip) {
    return <None selectedDate={selectedDate} />;
  }

  // Default 화면
  return (
    <Default
      vin={vin}
      selectedDate={selectedDate}
    />
  );
}

function None({selectedDate}: {selectedDate: Date}) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const today = new Date();

  return (
    <>
      <div className="driving_summary">
        <div className="driving_date">
          <span className="date_text">{CldrDatetimeFormatter.format(selectedDate, 'MMMMEEEEd')}</span>
          {isSameDate(today, selectedDate) && <span className="date_today">{t('T10008')}</span>}
        </div>
      </div>
      <div className="trip_info">
        <span className="blind">trip info detail</span>
        <div className="empty_area">
          <span className="ic ic_caution_32px" />
          <span className="body_small_r">{t('T10067')}</span>
        </div>
      </div>
    </>
  );
}

function Default({vin, selectedDate}: DrivingInfoTripContentProps) {
  const today = new Date();
  const {t} = useInferableTranslation();
  const navigate = useNavigate();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const DashedDatetimeFormatter = useDashedDatetimeFormatter();
  const {convertDistanceValue, convertDistanceSymbol, convertSpeedValue, convertSpeedSymbol} = useUnitConverter();
  const {checkAvailableComponent} = useAvailableComponentKr({vin});
  const {summaryData, totalDrivingHours, totalDrivingMinutes, formattedRecentTripDetail} = useTripDetailKr({
    vin,
    selectedDate,
  });

  const ignitionOnDate = parseDateWithFormat(formattedRecentTripDetail.ignOnTime);
  const ignitionOffDate = parseDateWithFormat(formattedRecentTripDetail.ignOffTime);

  if (!ignitionOnDate || !ignitionOffDate) {
    throw new ExpectedError('ignitionOnDate or ignitionOffDate error');
  }

  return (
    <>
      <div className="driving_summary">
        <div className="driving_date">
          <span className="date_text">{CldrDatetimeFormatter.format(selectedDate, 'MMMMEEEEd')}</span>
          {isSameDate(today, selectedDate) && <span className="date_today">{t('T10008')}</span>}
        </div>
        <dl className="driving_summary_list">
          <div className="driving_summary_item">
            <dt className="driving_summary_title">{t('T10053')}</dt>
            <dd className="driving_summary_info">
              {!!totalDrivingHours && (
                <>
                  <span className="driving_summary_value">{totalDrivingHours}</span>
                  <span className="driving_summary_unit">{t('T10054')}</span>
                </>
              )}
              {!!totalDrivingMinutes && (
                <>
                  <span className="driving_summary_value">{totalDrivingMinutes}</span>
                  <span className="driving_summary_unit">{t('T10055')}</span>
                </>
              )}
              {!totalDrivingHours && !totalDrivingMinutes && <span className="driving_summary_value">{t('T10202')}</span>}
            </dd>
          </div>
          <div className="driving_summary_item">
            <dt className="driving_summary_title">{t('T10052')}</dt>
            <dd className="driving_summary_info">
              {summaryData.totalDrvDist < 1 ? (
                <span className="driving_summary_value">{t('T20341')}</span>
              ) : (
                <>
                  <span className="driving_summary_value">{formatNumberWithCommas(convertDistanceValue(summaryData.totalDrvDist))}</span>
                  <span className="driving_summary_unit">{convertDistanceSymbol(summaryData.totalDrvDist)}</span>
                </>
              )}
            </dd>
          </div>
          {checkAvailableComponent(PermissionTypeKr.SAFETY_SCORE) && (
            <div className="driving_summary_item">
              <dt className="driving_summary_title">{t('T10057')}</dt>
              <dd className="driving_summary_info">
                {summaryData.safetyDrvScoreDiff >= 0 ? (
                  <span className="driving_summary_compare type_up">
                    <span className="ic ic_di_count_up">
                      <span className="blind">{t('T10059')}</span>
                    </span>
                    <span className="driving_summary_value">{formatNumberWithCommas(Math.abs(summaryData.safetyDrvScoreDiff))}</span>
                  </span>
                ) : (
                  <span className="driving_summary_compare type_down">
                    <span className="ic ic_di_count_down">
                      <span className="blind">{t('T10058')}</span>
                    </span>
                    <span className="driving_summary_value">{formatNumberWithCommas(Math.abs(summaryData.safetyDrvScoreDiff))}</span>
                  </span>
                )}
                <span className="driving_summary_value">{formatNumberWithCommas(summaryData.safetyDrvScore)}</span>
                <span className="driving_summary_unit">{t('T10056')}</span>
              </dd>
            </div>
          )}
        </dl>
      </div>
      <div className="trip_info type_badge">
        <span className="blind">trip info detail</span>
        <div className="trip_info_head">
          <span className="trip_info_item">
            {DashedDatetimeFormatter.formatRange(ignitionOnDate, ignitionOffDate, 'time').replace('~', '-')}
          </span>
          {formattedRecentTripDetail.tags
            ?.filter(({tagType}) => tagType === 'BUSINESS')
            .map(({tagName}) => (
              <span
                className="badge badge_basic_container"
                key={tagName}
              >
                {tagName}
              </span>
            ))}
        </div>
      </div>
      <div className="statistics_record_area">
        <ul className="statistics_record_list">
          <li className="statistics_record_item">
            <span className="statistics_record_key">
              <span className="ic ic_time_16px" />
              {t('T10053')}
            </span>
            <span className="statistics_record_val">
              {!formattedRecentTripDetail.intervalIgnitionHours && !formattedRecentTripDetail.intervalIgnitionMinutes && (
                <span className="statistics_record_unit">{t('T10202')}</span>
              )}
              {!!formattedRecentTripDetail.intervalIgnitionHours && (
                <>
                  <span className="statistics_record_number">{formattedRecentTripDetail.intervalIgnitionHours}</span>
                  <span className="statistics_record_unit">{t('T10054')}</span>
                </>
              )}
              {!!formattedRecentTripDetail.intervalIgnitionMinutes && (
                <>
                  <span className="statistics_record_number">{formattedRecentTripDetail.intervalIgnitionMinutes}</span>
                  <span className="statistics_record_unit">{t('T10055')}</span>
                </>
              )}
            </span>
          </li>
          <li
            className="statistics_record_item"
            role="text"
            aria-label={`${t('T10052')} ${
              formattedRecentTripDetail.drvDist < 1
                ? t('T20341')
                : formatNumberWithCommas(convertDistanceValue(formattedRecentTripDetail.drvDist)) +
                  convertDistanceSymbol(formattedRecentTripDetail.drvDist)
            }`}
          >
            <span className="statistics_record_key">
              <span className="ic ic_drive_distance_16px" />
              {t('T10052')}
            </span>
            <span className="statistics_record_val">
              {formattedRecentTripDetail.drvDist < 1 ? (
                <span className="statistics_record_unit">{t('T20341')}</span>
              ) : (
                <>
                  <span className="statistics_record_number">
                    {formatNumberWithCommas(convertDistanceValue(formattedRecentTripDetail.drvDist))}
                  </span>
                  <span className="statistics_record_unit">{convertDistanceSymbol(formattedRecentTripDetail.drvDist)}</span>
                </>
              )}
            </span>
          </li>
          <li
            className="statistics_record_item"
            role="text"
            aria-label={`${t('T10205')} ${formatNumberWithCommas(convertSpeedValue(formattedRecentTripDetail.avgSpeed))}${convertSpeedSymbol(formattedRecentTripDetail.avgSpeed)}`}
          >
            <span className="statistics_record_key">
              <span className="ic ic_avgspeed_16px" />
              {t('T10205')}
            </span>
            <span className="statistics_record_val">
              <span className="statistics_record_number">
                {formatNumberWithCommas(convertSpeedValue(formattedRecentTripDetail.avgSpeed))}
              </span>
              <span className="statistics_record_unit">{convertSpeedSymbol(formattedRecentTripDetail.avgSpeed)}</span>
            </span>
          </li>
        </ul>
        <button
          type="button"
          className="btn_learn_more"
          onClick={() => navigate(ROUTE_PATHS.HISTORY_$TRIP_ID.getFullPath(vin, formattedRecentTripDetail.tripId))}
        >
          {t('T10063')}
          <span className="ic ic_arrow_forward_12px" />
        </button>
      </div>
    </>
  );
}

function FallbackLoading() {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {defaultDistanceSymbol} = useUnitConverter();
  const today = new Date();

  return (
    <>
      <div className="driving_summary">
        <div className="driving_date skeleton">
          <span className="date_text">{CldrDatetimeFormatter.format(today, 'MMMMEEEEd')}</span>
          <span className="date_today">{t('T10008')}</span>
        </div>
        <dl className="driving_summary_list type_error skeleton">
          <div className="driving_summary_item">
            <dt className="driving_summary_title">{t('T10053')}</dt>
            <dd className="driving_summary_info">
              <span className="driving_summary_value">{ERROR.DASH}</span>
              <span className="driving_summary_unit">{t('T10054')}</span>
              <span className="driving_summary_value">{ERROR.DASH}</span>
              <span className="driving_summary_unit">{t('T10055')}</span>
            </dd>
          </div>
          <div className="driving_summary_item">
            <dt className="driving_summary_title">{t('T10052')}</dt>
            <dd className="driving_summary_info">
              <span className="driving_summary_value">{ERROR.DASH}</span>
              <span className="driving_summary_unit">{defaultDistanceSymbol}</span>
            </dd>
          </div>
          <div className="driving_summary_item">
            <dt className="driving_summary_title">{t('T10057')}</dt>
            <dd className="driving_summary_info">
              <span className="driving_summary_value">{ERROR.DASH}</span>
              <span className="driving_summary_unit">{t('T10056')}</span>
            </dd>
          </div>
        </dl>
      </div>
      <div
        className="trip_info skeleton"
        style={{height: '126px'}}
      >
        <span className="blind">trip info detail</span>
        <div className="empty_area">
          <span className="ic ic_caution_32px" />
          <span
            className="body_small_r"
            style={{whiteSpace: 'pre-wrap'}}
          >
            {t('T10175')}
          </span>
        </div>
      </div>
    </>
  );
}

function FallbackError({selectedDate}: {selectedDate: Date}) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {defaultDistanceSymbol} = useUnitConverter();
  const today = new Date();

  return (
    <>
      <div className="driving_summary">
        <div className="driving_date">
          <span className="date_text">{CldrDatetimeFormatter.format(selectedDate, 'MMMMEEEEd')}</span>
          {isSameDate(today, selectedDate) && <span className="date_today">{t('T10008')}</span>}
        </div>
        <dl className="driving_summary_list type_error">
          <div className="driving_summary_item">
            <dt className="driving_summary_title">{t('T10053')}</dt>
            <dd className="driving_summary_info">
              <span className="driving_summary_value">{ERROR.DASH}</span>
              <span className="driving_summary_unit">{t('T10054')}</span>
              <span className="driving_summary_value">{ERROR.DASH}</span>
              <span className="driving_summary_unit">{t('T10055')}</span>
            </dd>
          </div>
          <div className="driving_summary_item">
            <dt className="driving_summary_title">{t('T10052')}</dt>
            <dd className="driving_summary_info">
              <span className="driving_summary_value">{ERROR.DASH}</span>
              <span className="driving_summary_unit">{defaultDistanceSymbol}</span>
            </dd>
          </div>
          <div className="driving_summary_item">
            <dt className="driving_summary_title">{t('T10057')}</dt>
            <dd className="driving_summary_info">
              <span className="driving_summary_value">{ERROR.DASH}</span>
              <span className="driving_summary_unit">{t('T10056')}</span>
            </dd>
          </div>
        </dl>
      </div>
      <div className="trip_info">
        <span className="blind">trip info detail</span>
        <div className="empty_area">
          <span className="ic ic_caution_32px" />
          <span
            className="body_small_r"
            style={{whiteSpace: 'pre-wrap'}}
          >
            {t('T10175')}
          </span>
        </div>
      </div>
    </>
  );
}
